import { Capacitor } from "@capacitor/core";
import type { StationV2 } from "@orvis/core";
import { trackEvent } from "./analytics.js";
import { createDoc } from "./firebase.js";

/**
 * "Report a broken channel". Writes `reports/{autoId}`, which the
 * `onStreamReport` Cloud Function turns into a re-probe + Slack alert.
 * The shape must match the create rule in backend/firestore.rules exactly.
 *
 * One report per station per device per day — the throttle lives in
 * localStorage so a frustrated tap-tap-tap doesn't fan out into writes.
 */

export type ReportReason = "not_playing" | "wrong_channel" | "poor_quality" | "other";
export const REPORT_REASONS: readonly ReportReason[] = ["not_playing", "wrong_channel", "poor_quality", "other"];

const STORAGE_KEY = "orvis:reported";
const THROTTLE_MS = 24 * 60 * 60 * 1000;

function reportKey(country: string, kind: string, stationId: string) {
  return `${country}:${kind}:${stationId}`;
}

function readThrottle(): Record<string, number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : {};
    return parsed && typeof parsed === "object" ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
}

export function alreadyReported(country: string, kind: "tv" | "radio", station: StationV2, now = Date.now()): boolean {
  const at = readThrottle()[reportKey(country, kind, station.id ?? station.name)];
  return typeof at === "number" && now - at < THROTTLE_MS;
}

function platformLabel(): string {
  const p = Capacitor.getPlatform();
  return document.documentElement.classList.contains("tv") ? `${p}-tv` : p;
}

export async function submitReport(opts: {
  country: string;
  kind: "tv" | "radio";
  station: StationV2;
  reason: ReportReason;
  url?: string;
}): Promise<void> {
  const stationId = (opts.station.id ?? opts.station.name).slice(0, 200);
  const data: Record<string, unknown> = {
    country: opts.country,
    kind: opts.kind,
    stationId,
    stationName: opts.station.name.slice(0, 200),
    reason: opts.reason,
    platform: platformLabel().slice(0, 20),
  };
  if (opts.url) data["url"] = opts.url.slice(0, 2000);
  await createDoc("reports", data);
  trackEvent("stream_report", { content_type: opts.kind, item_id: stationId, reason: opts.reason });

  const now = Date.now();
  const next = readThrottle();
  for (const [k, at] of Object.entries(next)) if (now - at >= THROTTLE_MS) delete next[k];
  next[reportKey(opts.country, opts.kind, stationId)] = now;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch { /* private mode — the server-side counter still dedupes Slack */ }
}
